import { useEffect, useRef } from 'react';
import useQueryManager from '@Util/hook/useQueryManager';
import { isEqual, isEmpty } from '@Util/utils';

/**
 * @param {object} options
 * @param {boolean} options.enable 是否啟用 query string 同步
 * @param {object} options.defaultParams 預設查詢參數
 * @returns {{ searchParams: object, setQuery: (filters: object) => void }}
 */
const useQuerySync = ({ enable, defaultParams }) => {
    const { searchParams, setQuery } = useQueryManager();
    const prevRef = useRef(defaultParams);
    const isSearchEmpty = isEmpty(searchParams);

    // URL 目前沒有查詢參數時，直接以 defaultParams 當作這一輪的查詢條件回傳，
    // 不用等下方 useEffect 把 URL 補寫完成、觸發下一輪 render 才拿得到預設值。
    // 避免 isFetchOnMount 自動載入的觸發時機被延後，跟使用者手動操作的順序產生競態。
    const resolvedSearchParams = isSearchEmpty ? defaultParams : searchParams;

    useEffect(() => {
        if (!enable) return;

        const hasChanged = !isEqual(prevRef.current, searchParams);

        if (hasChanged) {
            const query = isSearchEmpty ? defaultParams : searchParams;
            delete query.mode; // 移除 mode 參數，不顯示在網址列
            setQuery(query); // 將預設或當前網址同步回網址列
            prevRef.current = query;
        }
    }, [enable, defaultParams, searchParams, setQuery, isSearchEmpty]);

    return {
        searchParams: enable ? resolvedSearchParams : defaultParams,
        setQuery: enable ? setQuery : () => {},
    };
};

export default useQuerySync;
